import { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, X, Check } from 'lucide-react';

// ─── Filter Types ─────────────────────────────────────────────────────

export type TransactionType =
  | 'Timesheet'
  | 'Purchase'
  | 'Expense'
  | 'DebitAdjustment'
  | 'CreditAdjustment'
  | 'SalesInvoice';

export type TransactionOrigin =
  | 'Timesheet'
  | 'PurchaseLedger'
  | 'ExpenseManagement'
  | 'NominalLedger'
  | 'SalesLedger';

export type PostType = 'C' | 'I' | 'S';

export interface TransactionFilters {
  searchTerm: string;
  projectCodes: string[];
  projectNames: string[];
  accountCodes: string[];
  accountNames: string[];
  resourceCodes: string[];
  resourceNames: string[];
  transactionTypes: TransactionType[];
  origins: TransactionOrigin[];
  postType: PostType | 'all';
  dateFrom: string;
  dateTo: string;
  batched: 'all' | 'true' | 'false';
  isInvoiced: 'all' | 'true' | 'false';
}

export const EMPTY_TRANSACTION_FILTERS: TransactionFilters = {
  searchTerm: '',
  projectCodes: [],
  projectNames: [],
  accountCodes: [],
  accountNames: [],
  resourceCodes: [],
  resourceNames: [],
  transactionTypes: [],
  origins: [],
  postType: 'all',
  dateFrom: '',
  dateTo: '',
  batched: 'all',
  isInvoiced: 'all',
};

// ─── Filter Options ───────────────────────────────────────────────────

const TRANSACTION_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Timesheet', label: 'Timesheet' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Expense', label: 'Expense' },
  { value: 'SalesInvoice', label: 'Sales Invoice' },
  { value: 'DebitAdjustment', label: 'Debit Adjustment' },
  { value: 'CreditAdjustment', label: 'Credit Adjustment' },
];

const ORIGIN_OPTIONS: { value: string; label: string }[] = [
  { value: 'Timesheet', label: 'Timesheet' },
  { value: 'PurchaseLedger', label: 'Purchase Ledger' },
  { value: 'ExpenseManagement', label: 'Expense Management' },
  { value: 'NominalLedger', label: 'Nominal Ledger' },
  { value: 'SalesLedger', label: 'Sales Ledger' },
];

const POST_TYPE_OPTIONS: { value: PostType | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'C', label: 'Cost (C)' },
  { value: 'I', label: 'Income (I)' },
  { value: 'S', label: 'Sales (S)' },
];

// ─── Helper Functions ─────────────────────────────────────────────────

/** Count active filters (excluding searchTerm which has its own clear in the toolbar) */
export function countActiveTransactionFilters(filters: TransactionFilters): number {
  let count = 0;
  if (filters.projectCodes.length > 0) count++;
  if (filters.projectNames.length > 0) count++;
  if (filters.accountCodes.length > 0) count++;
  if (filters.accountNames.length > 0) count++;
  if (filters.resourceCodes.length > 0) count++;
  if (filters.resourceNames.length > 0) count++;
  if (filters.transactionTypes.length > 0) count++;
  if (filters.origins.length > 0) count++;
  if (filters.postType !== 'all') count++;
  if (filters.dateFrom) count++;
  if (filters.dateTo) count++;
  if (filters.batched !== 'all') count++;
  if (filters.isInvoiced !== 'all') count++;
  return count;
}

// ─── Section Divider ──────────────────────────────────────────────────

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────

interface TransactionFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TransactionFilters;
  onApply: (filters: TransactionFilters) => void;
  onClear: () => void;
  /** Known values from data for building multi-select option lists */
  knownValues?: {
    projectCodes?: string[];
    projectNames?: string[];
    accountCodes?: string[];
    accountNames?: string[];
    resourceCodes?: string[];
    resourceNames?: string[];
  };
}

export function TransactionFilterSheet({
  open,
  onOpenChange,
  filters: appliedFilters,
  onApply,
  onClear,
  knownValues,
}: TransactionFilterSheetProps) {
  // Local draft state so changes are not applied until "Apply" is clicked
  const [draft, setDraft] = useState<TransactionFilters>({ ...EMPTY_TRANSACTION_FILTERS });

  // Sync draft from applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({ ...appliedFilters });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const draftActiveCount = useMemo(() => countActiveTransactionFilters(draft), [draft]);
  const appliedActiveCount = useMemo(() => countActiveTransactionFilters(appliedFilters), [appliedFilters]);

  // Build option lists from known values, merging with currently-selected values
  // so already-selected items always appear even if not in the current page's data
  const projectCodeOptions = useMemo(() => {
    const all = new Set([...(knownValues?.projectCodes ?? []), ...draft.projectCodes]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.projectCodes, draft.projectCodes]);

  const projectNameOptions = useMemo(() => {
    const all = new Set([...(knownValues?.projectNames ?? []), ...draft.projectNames]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.projectNames, draft.projectNames]);

  const accountCodeOptions = useMemo(() => {
    const all = new Set([...(knownValues?.accountCodes ?? []), ...draft.accountCodes]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.accountCodes, draft.accountCodes]);

  const accountNameOptions = useMemo(() => {
    const all = new Set([...(knownValues?.accountNames ?? []), ...draft.accountNames]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.accountNames, draft.accountNames]);

  const resourceCodeOptions = useMemo(() => {
    const all = new Set([...(knownValues?.resourceCodes ?? []), ...draft.resourceCodes]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.resourceCodes, draft.resourceCodes]);

  const resourceNameOptions = useMemo(() => {
    const all = new Set([...(knownValues?.resourceNames ?? []), ...draft.resourceNames]);
    return [...all].sort().map(v => ({ value: v, label: v }));
  }, [knownValues?.resourceNames, draft.resourceNames]);

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    setDraft({ ...EMPTY_TRANSACTION_FILTERS, searchTerm: appliedFilters.searchTerm });
    onClear();
    onOpenChange(false);
  };

  const handleResetDraft = () => {
    setDraft({ ...EMPTY_TRANSACTION_FILTERS, searchTerm: appliedFilters.searchTerm });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[450px] sm:w-[450px] sm:max-w-none p-0 flex flex-col h-full gap-0"
      >
        {/* Fixed Header */}
        <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b">
          <SheetTitle className="text-base flex items-center gap-2">
            Filters
            <Badge variant="secondary" className="ml-2 flex items-center gap-1.5 px-2.5 py-0.5">
              <Filter className="h-3 w-3" />
              {appliedActiveCount} {appliedActiveCount === 1 ? 'filter' : 'filters'} active
            </Badge>
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Refine your transaction view with advanced filters.
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <ScrollAreaViewport className="h-full max-h-full">
            <div className="px-6 py-4 space-y-3">
              {/* Project */}
              <div className="space-y-3">
                <SectionDivider label="Project" />
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Project Code</Label>
                    <MultiSelect
                      options={projectCodeOptions}
                      placeholder="Select project codes..."
                      emptyMessage="Type to search project codes."
                      maxDisplay={2}
                      value={draft.projectCodes}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, projectCodes: values }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Project Name</Label>
                    <MultiSelect
                      options={projectNameOptions}
                      placeholder="Select project names..."
                      emptyMessage="Type to search project names."
                      maxDisplay={2}
                      value={draft.projectNames}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, projectNames: values }))}
                    />
                  </div>
                </div>
              </div>

              {/* Account */}
              <div className="space-y-3">
                <SectionDivider label="Account" />
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Account Code</Label>
                    <MultiSelect
                      options={accountCodeOptions}
                      placeholder="Select account codes..."
                      emptyMessage="Type to search account codes."
                      maxDisplay={2}
                      value={draft.accountCodes}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, accountCodes: values }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Account Name</Label>
                    <MultiSelect
                      options={accountNameOptions}
                      placeholder="Select account names..."
                      emptyMessage="Type to search account names."
                      maxDisplay={2}
                      value={draft.accountNames}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, accountNames: values }))}
                    />
                  </div>
                </div>
              </div>

              {/* Resource */}
              <div className="space-y-3">
                <SectionDivider label="Resource" />
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Resource Code</Label>
                    <MultiSelect
                      options={resourceCodeOptions}
                      placeholder="Select resource codes..."
                      emptyMessage="Type to search resource codes."
                      maxDisplay={2}
                      value={draft.resourceCodes}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, resourceCodes: values }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Resource Name</Label>
                    <MultiSelect
                      options={resourceNameOptions}
                      placeholder="Select resource names..."
                      emptyMessage="Type to search resource names."
                      maxDisplay={2}
                      value={draft.resourceNames}
                      onValueChange={(values) => setDraft(prev => ({ ...prev, resourceNames: values }))}
                    />
                  </div>
                </div>
              </div>

              {/* Transaction Type */}
              <div className="space-y-3">
                <SectionDivider label="Transaction Type" />
                <MultiSelect
                  options={TRANSACTION_TYPE_OPTIONS}
                  placeholder="Select types..."
                  emptyMessage="No types found."
                  maxDisplay={3}
                  value={draft.transactionTypes}
                  onValueChange={(values) => setDraft(prev => ({ ...prev, transactionTypes: values as TransactionType[] }))}
                />
              </div>

              {/* Origin */}
              <div className="space-y-3">
                <SectionDivider label="Origin" />
                <MultiSelect
                  options={ORIGIN_OPTIONS}
                  placeholder="Select origins..."
                  emptyMessage="No origins found."
                  maxDisplay={3}
                  value={draft.origins}
                  onValueChange={(values) => setDraft(prev => ({ ...prev, origins: values as TransactionOrigin[] }))}
                />
              </div>

              {/* Post Type */}
              <div className="space-y-3">
                <SectionDivider label="Post Type" />
                <div className="space-y-2">
                  <Select
                    value={draft.postType}
                    onValueChange={(value) => setDraft(prev => ({ ...prev, postType: value as PostType | 'all' }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POST_TYPE_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date Range */}
              <div className="space-y-3">
                <SectionDivider label="Date Range" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="txf-dateFrom" className="text-xs font-semibold">From</Label>
                    <Input
                      id="txf-dateFrom"
                      type="date"
                      className="h-8 text-xs"
                      value={draft.dateFrom}
                      onChange={(e) => setDraft(prev => ({ ...prev, dateFrom: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="txf-dateTo" className="text-xs font-semibold">To</Label>
                    <Input
                      id="txf-dateTo"
                      type="date"
                      className="h-8 text-xs"
                      value={draft.dateTo}
                      onChange={(e) => setDraft(prev => ({ ...prev, dateTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-3">
                <SectionDivider label="Status" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="txf-batched" className="text-xs font-semibold">Batched</Label>
                    <Select
                      value={draft.batched}
                      onValueChange={(value) => setDraft(prev => ({ ...prev, batched: value as 'all' | 'true' | 'false' }))}
                    >
                      <SelectTrigger id="txf-batched" className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="txf-isInvoiced" className="text-xs font-semibold">Invoiced</Label>
                    <Select
                      value={draft.isInvoiced}
                      onValueChange={(value) => setDraft(prev => ({ ...prev, isInvoiced: value as 'all' | 'true' | 'false' }))}
                    >
                      <SelectTrigger id="txf-isInvoiced" className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Fixed Footer */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="gap-2"
            disabled={appliedActiveCount === 0 && draftActiveCount === 0}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={handleApply} className="gap-2">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
